import { useState } from 'react';
import type { ImportProfileSummary, ImportUpload } from '@ni/shared';
import { useReplaceImportFile } from '../../api/hooks';
import {
  IMPORT_CONTAINER_LABELS,
  IMPORT_FILE_ACCEPT,
  IMPORT_FILE_DESCRIPTION_HEAD,
  IMPORT_FILE_HINT,
  IMPORT_FILE_HISTORY_HEAD,
  IMPORT_FILE_LOCKED_NOTE,
  IMPORT_FILE_PROFILE_HEAD,
  IMPORT_FILE_PROFILE_UNAVAILABLE,
  IMPORT_FILE_REPLACE,
  IMPORT_FILE_REPLACING,
  SOMEONE_HERE,
  importFileSize,
  importFileTooBig,
} from '../../lib/copy';
import { formatDay, joinClauses, plural } from '../../lib/format';
import { ErrorLine } from '../../components/ui/error-line';
import { FilePicker } from '../../components/imports/file-picker';
import { WorkingButton } from '../../components/ui/working-button';
import { useImportPage } from './session';
import { EmptyLine } from '../../components/ui/empty-line';
import { Section } from '../../components/ui/section';
import { Field } from '../../components/ui/field';

/**
 * **The File tab** (import note §2.4): which bytes this session is reading, what
 * is in them, who put them there — and, while nothing has been written, the way
 * to put different ones there instead.
 *
 * Two of the four things §2.4 asks this tab for used to be missing, and the tab
 * said so rather than quietly omitting them: what the file *holds* and every
 * earlier upload were on the session's row and not on the wire. Both are on the
 * session read now — `profile_summary` and `files` — so the tab says all four.
 *
 * **Replacing turns on `committed`, not on the phase.** The server refuses once
 * there is a commit decision (`IMPORT_FILE_LOCKED`), and the tab was asking
 * `phase !== 'importing'`, which is false the moment a commit stops moving: a
 * session whose commit had failed offered the file picker back, and the upload
 * met a 409.
 */
export function ImportFileTab() {
  const { importId, session } = useImportPage();
  const replace = useReplaceImportFile(importId);
  const [chosen, setChosen] = useState<File | null>(null);

  /* The file is the session's until the session has written something with it —
     which is exactly what the server refuses past (`IMPORT_FILE_LOCKED`). */
  const replaceable = session.state === 'in_progress' && !session.committed;
  const tooBig = chosen === null ? null : importFileTooBig(chosen.size, chosen.name);

  return (
    <div className="import-file">
      <Section title="The file">
        <div className="act-row">
          <div className="act-name">{session.file.filename}</div>
          <div className="act-value fill">
            {joinClauses([
              IMPORT_CONTAINER_LABELS[session.file.container],
              importFileSize(session.file.size),
              `uploaded by ${session.created_by.name ?? SOMEONE_HERE.toLowerCase()}`,
              formatDay(session.created_at),
            ])}
          </div>
        </div>
        <div className="src">{session.file.sha256}</div>
      </Section>

      {/* **The description, whole** (§2.4). It is the sentence the planner was
          given and it is the only account of what this file is *for*, so
          somewhere has to hold all of it. The head and the ledger row clamp it
          to two lines — a board's description runs to a paragraph — and this is
          the somewhere. */}
      <Section title={IMPORT_FILE_DESCRIPTION_HEAD}>
        <p className="lede">{session.description}</p>
      </Section>

      <ProfileSummary profile={session.profile_summary} />
      <UploadHistory uploads={session.files} />

      {replaceable ? (
        <Field label={IMPORT_FILE_REPLACE} htmlFor="import-replace-file">
          <FilePicker
            id="import-replace-file"
            accept={IMPORT_FILE_ACCEPT}
            disabled={replace.isPending}
            chosen={chosen}
            hint={IMPORT_FILE_HINT}
            onChoose={setChosen}
          />
          {tooBig ? <div className="src">{tooBig}</div> : null}
          <div className="layer-actions">
            <WorkingButton
              label={IMPORT_FILE_REPLACE}
              workingLabel={IMPORT_FILE_REPLACING}
              variant="act"
              working={replace.isPending}
              disabled={chosen === null || tooBig !== null}
              onClick={() => {
                if (chosen && tooBig === null) {
                  replace.mutate(chosen, { onSuccess: () => setChosen(null) });
                }
              }}
            />
          </div>
          {/* Replacing rebuilds the profile and the rows; the plan is kept and is
              checked against the new columns, so the office is told to look. */}
          <div className="src">
            The rows are read again from the new file. The plan is kept, so look at it before
            previewing.
          </div>
          {replace.isError ? <ErrorLine error={replace.error} /> : null}
        </Field>
      ) : (
        <div className="src">{IMPORT_FILE_LOCKED_NOTE}</div>
      )}
    </div>
  );
}

/**
 * **What the file holds** — the sheets, how much of each is people, what was
 * left out, the group headings the status rules are written against, and each
 * column with three of its cells.
 *
 * A summary and not the profile: the whole thing is the planner's document and
 * is most of a megabyte on a two-sheet board, and this page is polled every
 * three seconds while a session is moving.
 */
export function ProfileSummary({ profile }: { profile: ImportProfileSummary }) {
  const nothing = profile.sheets.length === 0 && profile.document === null;

  return (
    <Section title={IMPORT_FILE_PROFILE_HEAD}>
      {nothing ? <EmptyLine>{IMPORT_FILE_PROFILE_UNAVAILABLE}</EmptyLine> : null}

      {profile.document ? (
        <div className="act-row">
          <div className="act-name">
            {plural(profile.document.heading_count, 'entry', 'entries')}
          </div>
          <div className="act-value fill">
            {joinClauses([
              profile.document.non_name_headings > 0
                ? `${profile.document.non_name_headings} headings name nobody`
                : null,
              `about ${plural(profile.document.body_length, 'word', 'words')} under each`,
            ])}
          </div>
        </div>
      ) : null}

      {profile.sheets.map((sheet) => (
        <div key={sheet.name}>
          <div className="act-row">
            <div className="act-name">{sheet.name}</div>
            <div className="act-value fill">
              {joinClauses([
                plural(sheet.data_row_count, 'person', 'people'),
                plural(sheet.column_count, 'column', 'columns'),
                sheet.group_titles.length > 0
                  ? plural(sheet.group_titles.length, 'group', 'groups')
                  : null,
              ])}
            </div>
          </div>

          {sheet.group_titles.length > 0 ? (
            <div className="src">
              {sheet.group_titles
                .map((group) => `${group.title} (${group.data_row_count})`)
                .join(' · ')}
            </div>
          ) : null}

          {sheet.exclusions.map((exclusion, index) => (
            <div className="src" key={`${sheet.name}-${exclusion.rule}-${index}`}>
              {exclusion.detail}: {plural(exclusion.count, 'row', 'rows')} left out
            </div>
          ))}

          <table className="table">
            <thead>
              <tr>
                <th scope="col">Column</th>
                <th scope="col">What is in it</th>
                <th scope="col">How full</th>
              </tr>
            </thead>
            <tbody>
              {sheet.columns.map((column) => (
                <tr key={column.index}>
                  <td>
                    {column.header.length > 0 ? column.header : 'unnamed'}
                    <div className="src">column {column.index}</div>
                  </td>
                  <td className="quiet-cell">{column.samples.join(' · ')}</td>
                  {/* A share of the rows, said as a share and never as a score:
                      "8 in 10" is what a person checks a mapping against, and
                      §0.7's rule is that no number on any surface is one. */}
                  <td className="quiet-cell">{fillWords(column.fill_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </Section>
  );
}

/** How much of a column is filled, in the office's words rather than a decimal. */
export function fillWords(rate: number): string {
  if (rate >= 0.999) return 'every row';
  if (rate <= 0.001) return 'empty';
  return `${Math.round(rate * 10)} in 10`;
}

/**
 * **Every upload the session has held**, oldest first — the history §2.4 asks
 * for, and the thing a replacement is only honest with.
 *
 * Each entry carries its *own* type and size. They were read off the session's
 * current file until 2026-09-06, so a history of two uploads printed today's
 * file twice: a history that restates the present for the past is worse than
 * none.
 */
export function UploadHistory({ uploads }: { uploads: readonly ImportUpload[] }) {
  return (
    <Section title={IMPORT_FILE_HISTORY_HEAD}>
      {uploads.length <= 1 ? (
        <EmptyLine>This file has not been replaced.</EmptyLine>
      ) : (
        uploads.map((upload, index) => (
          <div className="act-row" key={`${upload.sha256}-${index}`}>
            <div className="act-name">{upload.filename}</div>
            <div className="act-value fill">
              {joinClauses([
                IMPORT_CONTAINER_LABELS[upload.container],
                importFileSize(upload.size),
                `uploaded by ${upload.uploaded_by.name ?? SOMEONE_HERE.toLowerCase()}`,
                formatDay(upload.at),
                index === uploads.length - 1 ? 'the one being read' : null,
              ])}
            </div>
          </div>
        ))
      )}
    </Section>
  );
}
